/**
 * SIEM Event Details Body – event metadata and JSON sections
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { SiemEvent } from '@/hooks';
import { formatDateTime } from '@/features/siem/utils/siem-utils';

function prettyJson(value: unknown): string {
  if (value === undefined) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export interface SiemEventDetailsBodyProps {
  event: SiemEvent;
}

export const SiemEventDetailsBody: FC<SiemEventDetailsBodyProps> = ({ event }) => (
  <>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
      <div><span className="text-muted-foreground">ID</span><div className="font-mono break-words">{event.id}</div></div>
      <div><span className="text-muted-foreground">Timestamp</span><div>{formatDateTime(event.timestamp)}</div></div>
      <div><span className="text-muted-foreground">Level</span><div className="font-mono">{event.level}</div></div>
      <div><span className="text-muted-foreground">Source / Category</span><div className="font-mono break-words">{event.source} / {event.category}</div></div>
      <div><span className="text-muted-foreground">User</span><div className="break-words">{event.userEmail || event.userId || '—'}</div></div>
      <div><span className="text-muted-foreground">IP</span><div className="font-mono break-words">{event.ipAddress || '—'}</div></div>
      <div className="md:col-span-2">
        <span className="text-muted-foreground">IP region (GeoIP)</span>
        <div className="mt-0.5 text-xs text-muted-foreground">
          Inferred from the HTTP client address (browser / VPN / proxy). Not the SMSWiser campaign
          country.
        </div>
        <div className="font-mono">{event.geoipCountry || '—'}</div>
      </div>
      <div><span className="text-muted-foreground">HTTP Method</span><div className="font-mono">{event.httpMethod || '—'}</div></div>
      <div><span className="text-muted-foreground">Browser</span><div className="font-mono break-words">{event.browser || '—'}</div></div>
      <div><span className="text-muted-foreground">OS</span><div className="font-mono break-words">{event.os || '—'}</div></div>
      <div className="md:col-span-2"><span className="text-muted-foreground">User-Agent</span><div className="font-mono break-words">{event.userAgent || '—'}</div></div>
    </div>
    {event.action ? (
      <div>
        <p className="text-sm font-medium">Action</p>
        <pre className="mt-2 text-xs rounded-lg border bg-muted/20 p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">{event.action}</pre>
      </div>
    ) : null}
    {(event.details as Record<string, unknown>)?.requestBody !== undefined ? (
      <div>
        <p className="text-sm font-medium">Request Body</p>
        <pre className="mt-2 text-xs rounded-lg border bg-muted/20 p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">
          {prettyJson((event.details as Record<string, unknown>).requestBody)}
        </pre>
      </div>
    ) : null}
    {(event.details as Record<string, unknown>)?.responseBody !== undefined ? (
      <div>
        <p className="text-sm font-medium">Response</p>
        <pre className="mt-2 text-xs rounded-lg border bg-muted/20 p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">
          {prettyJson((event.details as Record<string, unknown>).responseBody)}
        </pre>
      </div>
    ) : null}
    <div>
      <p className="text-sm font-medium">Details</p>
      <pre className="mt-2 text-xs rounded-lg border bg-muted/20 p-3 overflow-x-auto whitespace-pre-wrap break-words font-mono">
        {prettyJson(event.details || {})}
      </pre>
    </div>
  </>
);
